/**
 * Rows for the Consultation no-show tab (BAR-2809). The query returns each
 * patient's `no-show-consult`; the patient is still judged on all their consults,
 * so a rebooked patient leaves before the tag catches up.
 */

import { getReferenceString } from '@medplum/core';
import { Appointment, HumanName, Patient, Practitioner } from '@medplum/fhirtypes';
import { ActiveLeadState } from '@bariendo/lib/active-lead';
import { getCareTeamMembersInPool } from '@bariendo/lib/care-team-roles';
import { countContactAttempts } from '@bariendo/lib/communication';
import { Track } from '@bariendo/lib/constants';
import { readEligibilityIntakeSelections, readEligibilityPayment } from '@bariendo/lib/eligibility-intake';
import { getTrackOrSelfPay } from '@bariendo/lib/episode-of-care';
import { readInterestedService, readMarketLocation } from '@bariendo/lib/intake-selection';
import { noteForAppointment, parsePatientAccessCallAnswers } from '@bariendo/lib/pac-call-notes';
import { readEmployer } from '@bariendo/lib/pac-inquiry-list';
import { noShowLead } from '@bariendo/lib/pac-no-show-list';
import { hydratedPractitionerNames, PacQueueHydration } from '../hooks/usePacQueueHydration';
import {
  activeLeadFor,
  ConsultAppointmentResult,
  hydratedAppointments,
  LastCommunicationCell,
  patientParticipant,
  StickyNoteCell,
  toLastCommunicationCell,
  toStickyNoteCell,
} from './listRowShared';

export interface NoShowLeadRow {
  id: string;
  patient: Patient;
  /** The latest missed consult. */
  consult: Appointment;
  missCount: number;
  patientNavigators: HumanName[];
  patientNavigatorPractitioners: Practitioner[];
  location?: string;
  procedure?: string;
  track?: Track;
  employer?: string;
  lastCommunication?: LastCommunicationCell;
  stickyNote?: StickyNoteCell;
  activeLead: ActiveLeadState;
}

export function buildNoShowRows(
  missedConsults: ConsultAppointmentResult[],
  hydration: PacQueueHydration,
  now: Date
): NoShowLeadRow[] {
  const rows: NoShowLeadRow[] = [];
  const seen = new Set<string>();

  for (const missed of missedConsults) {
    const patient = patientParticipant(missed.participant);
    const patientRef = patient?.id ? getReferenceString(patient) : undefined;
    if (!patient?.id || !patientRef || seen.has(patientRef)) {
      continue;
    }
    seen.add(patientRef);

    const lead = noShowLead(hydration.consultsByPatient.get(patientRef) ?? [], now);
    if (!lead?.consult.id) {
      continue;
    }

    const callNotes = hydration.callNotesByPatient.get(patientRef);
    const locationNote =
      noteForAppointment(callNotes, getReferenceString({ resourceType: 'Appointment', id: lead.consult.id })) ??
      callNotes?.[0];
    const locationAnswers = locationNote ? parsePatientAccessCallAnswers(locationNote) : undefined;
    const eligibility = hydration.eligibilityByPatient.get(patientRef);
    const eligibilitySelections = readEligibilityIntakeSelections(eligibility);
    const episode = hydration.episodeByPatient.get(patientRef);
    const navigatorRefs = getCareTeamMembersInPool(
      hydration.careTeamByPatient.get(patientRef),
      hydration.patientNavigatorPool
    );
    const lastCommunication = hydration.lastCommunicationByPatient.get(patientRef);
    const lastCommunicationCell = toLastCommunicationCell(lastCommunication);

    rows.push({
      id: patient.id,
      patient,
      consult: lead.consult,
      missCount: lead.missCount,
      patientNavigators: hydratedPractitionerNames(navigatorRefs, hydration.practitionerByRef),
      patientNavigatorPractitioners: navigatorRefs
        .map((reference) => hydration.practitionerByRef.get(reference))
        .filter((practitioner): practitioner is Practitioner => practitioner !== undefined),
      location: readMarketLocation(patient, locationAnswers, eligibilitySelections.market),
      procedure: readInterestedService(patient, locationAnswers, eligibilitySelections.procedure),
      track: getTrackOrSelfPay(episode),
      employer: readEmployer(patient) ?? readEligibilityPayment(eligibility).employer,
      lastCommunication: lastCommunicationCell && {
        ...lastCommunicationCell,
        // Counted since the missed consult on No-show.
        attempts: countContactAttempts(hydration.communicationsByPatient.get(patientRef), lead.consult.start),
      },
      stickyNote: toStickyNoteCell(hydration.stickyNoteByPatient.get(patientRef), hydration.practitionerByRef),
      activeLead: activeLeadFor({
        lastCommunicationSent: lastCommunication?.sent,
        leadFlags: hydration.flagsByPatient.get(patientRef),
        signupAt: episode?.period?.start,
        appointments: [lead.consult, ...hydratedAppointments(hydration, patientRef)],
        now,
      }),
    });
  }

  return rows;
}
